import {
  PASTE_ANALYSIS_BODY_LIMIT,
  PasteAnalysisParamsSchema,
  PasteAnalysisCreateBodySchema,
  PasteAnalysisReceiptSchema,
  PasteAnalysisGetSchema,
  PasteFeedbackBodySchema,
  PasteFeedbackAcceptedSchema,
} from '../minutes/smart-paste.ts';
import {
  DocumentManagerListSchema,
  DocumentManagerNodeSchema,
  CreateManagerFolderBodySchema,
  UpdateManagerNodeBodySchema,
  ManagerFavoriteBodySchema,
  ManagerBatchBodySchema,
  ManagerActivitySchema,
  CreateManagerCommentBodySchema,
  UpdateManagerCommentBodySchema,
  DeleteManagerCommentBodySchema,
  ManagerCommentParamsSchema,
} from './document-manager.ts';
// Таблица маршрутов API — по contracts/api.md 001, 002, 003, 005, 008, 010 и 011. Сервер регистрирует маршруты по ней,
// рабочее приложение строит по ней клиент, OpenAPI (T057, T051) собирается из тех же схем.
import type * as z from 'zod';
import { AssignmentSchema } from '../access/assignment.ts';
import { SupportGrantSchema } from '../access/support-grant.ts';
import {
  IssuedInviteSchema,
  OwnSessionsSchema,
  UserDetailSchema,
  UserSchema,
  UserWithAssignmentsSchema,
} from '../access/user.ts';
import { errorCodes } from '../errors/catalog.ts';
import type { ErrorCode } from '../errors/catalog.ts';
import { IdParamsSchema, IfMatchHeadersSchema } from '../kernel/common.ts';
import type { Operation } from '../rights/matrix.ts';
import {
  AssignmentListSchema,
  AssignmentVersionHeadersSchema,
  CreateAssignmentBodySchema,
  UpdateAssignmentBodySchema,
} from './assignments.ts';
import { AuditExportSchema } from '../audit/export.ts';
import {
  BackupsResponseSchema,
  HealthBriefResponseSchema,
  StatusResponseSchema,
} from '../observability/status.ts';
import { JobSchema } from '../observability/job.ts';
import { MailConnectionViewSchema } from '../delivery/connection.ts';
import {
  CreateTemplateBodySchema,
  OnboardingDraftSchema,
  OnboardingRowsBodySchema,
  RowsViewSchema,
} from '../protocol/onboarding.ts';
import {
  NewTemplateVersionBodySchema,
  OnboardingAnalyzeQuerySchema,
  TemplateCardSchema,
  TemplateListQuerySchema,
  TemplateListSchema,
  UpdateTemplateBodySchema,
} from './templates.ts';
import { MailMessageCardSchema } from '../delivery/message.ts';
import { SendWelcomeResponseSchema, WelcomeLetterResponseSchema } from '../delivery/welcome.ts';
import { ProtocolStateSchema, TransferPreviewSchema } from '../minutes/protocol.ts';
import {
  COMMAND_BODY_LIMIT,
  CommandAcceptedSchema,
  CommandSchema,
  RevertBodySchema,
  TransferBodySchema,
} from '../minutes/command.ts';
import { ProtocolViewPatchSchema, ProtocolViewSchema } from '../minutes/view.ts';
import {
  CreateProtocolBodySchema,
  CreatedProtocolSchema,
  LeadCandidatesQuerySchema,
  LeadCandidatesResponseSchema,
  PeopleSearchQuerySchema,
  PeopleSearchResponseSchema,
  AssigneeOptionsQuerySchema,
  AssigneeOptionsResponseSchema,
} from '../minutes/fields.ts';
import {
  CreateLeadBodySchema,
  CreateLeadResponseSchema,
  LeadLookupQuerySchema,
  LeadLookupResponseSchema,
  LeadStateSchema,
  SetLeadBodySchema,
  SetLeadResponseSchema,
} from '../minutes/lead.ts';
import {
  PartiesListQuerySchema,
  PartyCreateBodySchema,
  PartyCreatedSchema,
  ProtocolPartiesResponseSchema,
} from '../minutes/parties.ts';
import { ChangesQuerySchema, ProtocolChangesSchema } from '../minutes/changes.ts';
import {
  ItemHistoryParamsSchema,
  ItemHistoryQuerySchema,
  ItemHistoryResponseSchema,
} from '../minutes/history.ts';
import { ProtocolRevisionsSchema, RevisionsQuerySchema } from '../minutes/revisions.ts';
import {
  SnapshotListSchema,
  SnapshotParamsSchema,
  SnapshotViewSchema,
} from '../minutes/snapshot.ts';
import {
  RestorePreviewSchema,
  RevertPreviewSchema,
  VersionListSchema,
  VersionNameBodySchema,
  VersionNameResponseSchema,
  ProtocolVersionParamsSchema,
  VersionSelectionSchema,
  VersionViewQuerySchema,
  VersionViewSchema,
  VersionsQuerySchema,
} from '../minutes/versions.ts';
import { ExportXlsxQuerySchema, PrintOptionsSchema } from '../minutes/print.ts';
import {
  AccessCandidatesQuerySchema,
  AccessCandidatesResponseSchema,
  GrantBodySchema,
  GrantParamsSchema,
  GrantUpdateBodySchema,
  InviteBodySchema,
  InviteResponseSchema,
  MineResponseSchema,
  ProtocolAccessViewSchema,
  ResendResponseSchema,
  SharingBodySchema,
  SharingIfMatchHeadersSchema,
  ZoneOptionsQuerySchema,
  ZoneOptionsResponseSchema,
} from '../minutes/access.ts';
import {
  AccessSummaryResponseSchema,
  BulkAccessBodySchema,
  BulkAccessResultSchema,
  UserProtocolAccessSchema,
} from '../minutes/access-summary.ts';
import {
  MailConnectionVersionHeadersSchema,
  MailMessagesPageSchema,
  MailMessagesQuerySchema,
  SaveMailConnectionBodySchema,
  SetMailKeyBodySchema,
  SetMailSendingBodySchema,
} from './delivery.ts';
import {
  LetterHistorySchema,
  LetterPreviewBodySchema,
  LetterPreviewSchema,
  LetterTemplateListSchema,
  LetterTemplateParamsSchema,
  LetterTemplateSchema,
  RestoreLetterTemplateBodySchema,
  UpdateLetterTemplateBodySchema,
} from './letter-templates.ts';
import {
  AuditExportQuerySchema,
  AuditExportsPageSchema,
  AuditExportsQuerySchema,
  AuditListQuerySchema,
  AuditPageSchema,
  CreateAuditExportBodySchema,
} from './audit.ts';
import {
  ChangePasswordBodySchema,
  LoginBodySchema,
  MeResponseSchema,
  PasswordRecoveryBodySchema,
  PasswordRecoveryResponseSchema,
} from './auth.ts';
import { AcceptInviteBodySchema, InviteInfoSchema, InviteTokenParamsSchema } from './invites.ts';
import {
  EmptyBodySchema,
  HelpBodySchema,
  LinkLoginResponseSchema,
  LoginByCodeBodySchema,
  LoginLinkInfoSchema,
  LoginLinkRequestedSchema,
  LoginLinkResendResponseSchema,
  LoginLinkTokenParamsSchema,
  RequestLoginLinkBodySchema,
} from '../auth/link.ts';
import {
  MyAnswerResultSchema,
  MyAnswerSchema,
  MyCommandParamsSchema,
  MyItemParamsSchema,
  MyItemsSchema,
  MyPeopleQuerySchema,
  MyPeopleResponseSchema,
  MyReportSchema,
  MySeenBodySchema,
  MySectionSchema,
  MySummarySchema,
} from '../control/my-items.ts';
import { FirstLetterViewSchema } from '../delivery/first-letter.ts';
import { MailSafetySchema } from '../delivery/safety.ts';
import {
  SetEmailsBodySchema,
  SetEmailsResultSchema,
  ShadowLettersSchema,
  TimeZoneBodySchema,
} from '../delivery/item-letter.ts';
import {
  MeetingDayBodySchema,
  MeetingDaySchema,
  ProtocolFunnelSchema,
  ProtocolReadinessSchema,
} from '../control/readiness.ts';
import {
  MailingEnableBodySchema,
  MailingSetBodySchema,
  ProtocolMailingSchema,
} from '../delivery/mailing.ts';
import {
  LinkCheckDecideBodySchema,
  LinkCheckRowSchema,
  LinkChecksQuerySchema,
  LinkChecksResponseSchema,
} from '../directory/link-check.ts';
import { NotificationsQuerySchema, NotificationsResponseSchema } from './notifications.ts';
import { AnswerEnterResultSchema, AnswerEnterSchema } from '../answers/answer.ts';
import { InboxCardSchema } from '../answers/inbox.ts';
import { TeamOverdueSchema } from '../control/team.ts';
import { AbsenceBodySchema, AbsenceSchema, PersonParamsSchema } from '../directory/absence.ts';
import {
  AlertsPageSchema,
  AlertsQuerySchema,
  BackupsQuerySchema,
  CancelJobBodySchema,
  DeploymentsResponseSchema,
  HealthResponseSchema,
  JobDetailSchema,
  JobsPageSchema,
  JobsQuerySchema,
  LogPageSchema,
  LogQuerySchema,
  NotReadyResponseSchema,
  ReadyResponseSchema,
} from './ops.ts';
import {
  ControlItemsQuerySchema,
  ControlItemsResponseSchema,
  ControlSheetQuerySchema,
  DocumentListSchema,
  DocumentRowSchema,
  DocumentVersionListSchema,
  PreviewQuerySchema,
  PreviewResponseSchema,
  UploadHeadersSchema,
  UploadQuerySchema,
  VersionParamsSchema,
} from './documents.ts';
import {
  CreateAliasBodySchema,
  CreateCuratorshipBodySchema,
  CreateOrganizationBodySchema,
  CreatePairBodySchema,
  CreatePersonBodySchema,
  CreateWorkplaceBodySchema,
  DirectorySearchQuerySchema,
  DirectorySearchResponseSchema,
  KindChangeBodySchema,
  KindChangePreviewQuerySchema,
  KindChangePreviewSchema,
  KindChangeResultSchema,
  KindDoubtsPageSchema,
  KindDoubtsQuerySchema,
  KindReviewBodySchema,
  KindReviewSchema,
  MentionsPageSchema,
  MentionsQuerySchema,
  OrganizationCardSchema,
  PairCardSchema,
  PairDecisionBodySchema,
  PairDecisionResultSchema,
  PairsPageSchema,
  PairsQuerySchema,
  PersonCardSchema,
  ReceivesMailBodySchema,
  RecordsPageSchema,
  RecordsQuerySchema,
  RemoveCuratorshipBodySchema,
  ResolveMentionBodySchema,
  SetEmailBodySchema,
  SetEmailResponseSchema,
  SetZoneBodySchema,
  TriageSchema,
  UpdateOrganizationBodySchema,
  UpdatePersonBodySchema,
  UpdateWorkplaceBodySchema,
  VersionDirectorySummarySchema,
  VersionSummaryParamsSchema,
  ZoneListSchema,
  ZoneParamsSchema,
  BulkBodySchema,
  BulkParamsSchema,
  BulkResultSchema,
} from './directory.ts';
import { AliasRejectionSchema, AliasSchema } from '../directory/alias.ts';
import { StaffStructureSchema } from '../directory/staff-structure.ts';
import {
  CreateUnitConversionBodySchema,
  UnitConversionParamsSchema,
  UnitConversionPreviewQuerySchema,
  UnitConversionPreviewSchema,
  UnitConversionSchema,
} from '../directory/unit-conversion.ts';
import { MentionRowSchema } from '../directory/mention.ts';
import { MergeSchema, PairSchema } from '../directory/pair.ts';
import { CuratorshipSchema, WorkplaceSchema, ZoneSchema } from '../directory/record.ts';
import {
  CreateUnitBodySchema,
  SetUnitEmailBodySchema,
  UnitArchivePreviewSchema,
  UnitArchiveResultSchema,
  UnitCardSchema,
  UnitParamsSchema,
  UpdateUnitBodySchema,
} from '../directory/unit.ts';
import {
  SettingHistorySchema,
  SettingParamsSchema,
  SettingSchema,
  SettingsResponseSchema,
  UpdateSettingBodySchema,
} from './settings.ts';
import {
  AgentKeyListSchema,
  AgentKeySchema,
  IssueAgentKeyBodySchema,
  IssuedAgentKeySchema,
} from './agent-keys.ts';
import { RightsResponseSchema } from './rights.ts';
import {
  CreateSupportGrantBodySchema,
  SupportGrantsPageSchema,
  SupportGrantsQuerySchema,
} from './support-access.ts';
import {
  ArchiveUserBodySchema,
  CreateUserBodySchema,
  CreateUserResponseSchema,
  UpdateUserBodySchema,
  UsersPageSchema,
  UsersQuerySchema,
} from './users.ts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * public — без сеанса (вход, проверка состояния);
 * invite — по ссылке-приглашению: токен в адресе не пишется в журнал запросов,
 *   ответ с Referrer-Policy: no-referrer (FR-003д);
 * session — в сеансе: cookie __Host-sid, право по матрице и правилу «в сеансе» (FR-011а).
 */
export type RouteAccess = 'public' | 'invite' | 'session';

export interface RouteDefinition {
  readonly method: HttpMethod;
  /** Путь в виде OpenAPI: /api/users/{id}. Для Fastify — toFastifyPath(). */
  readonly path: `/api/${string}`;
  readonly access: RouteAccess;
  /**
   * Операции матрицы прав: доступ есть, если разрешена хотя бы одна. Какие записи видны
   * («области», «сам», «по доступу»), сервер решает по правилу каждой операции.
   */
  readonly operations: readonly Operation[];
  readonly params?: z.ZodType;
  readonly query?: z.ZodType;
  /** Имена заголовков — строчными буквами, как их отдаёт Fastify. */
  readonly headers?: z.ZodType;
  readonly body?: z.ZodType;
  /**
   * Тело — файл как есть, а не JSON (003): xlsx — книга Excel с типом XLSX_CONTENT_TYPE
   * не больше PROTOCOL_FILE_MAX_BYTES; больше — 413 DOC-003.
   */
  readonly upload?: 'xlsx';
  /**
   * Предел тела JSON, байт, если он больше общего (012: команды таблицы — до 500 правок,
   * research, Р18). Больше — отказ до разбора.
   */
  readonly bodyLimit?: number;
  /** Код успешного ответа. */
  readonly status: 200 | 201 | 202 | 204;
  /** Схема тела успешного ответа; нет — пустой ответ или файл. */
  readonly response?: z.ZodType;
  /**
   * Ответ — файл Excel, текст (метрики Prometheus), страница HTML (лист контроля 003, печатная
   * форма 016: text/html; charset=utf-8, без скриптов) или JSON-вложение (JSON-копия протокола
   * 016: application/json; charset=utf-8), а не ответ по схеме.
   */
  readonly file?: 'xlsx' | 'text' | 'html' | 'json';
  /**
   * Схема тела 503 вместо ошибки SYS-001: «готов» отвечает «не готов» без кода и причины
   * (002, FR-037, FR-038).
   */
  readonly unavailable?: z.ZodType;
  /** Ошибки из api.md; общие для всех маршрутов добавляет routeErrors(). */
  readonly errors: readonly ErrorCode[];
}

export const routes = {
  // Вход и сеанс
  'auth.login': {
    method: 'POST',
    path: '/api/auth/login',
    access: 'public',
    operations: [],
    body: LoginBodySchema,
    status: 204,
    errors: ['AUTH-001', 'AUTH-002', 'AUTH-009'],
  },
  'auth.logout': {
    method: 'POST',
    path: '/api/auth/logout',
    access: 'session',
    operations: ['auth.session'],
    status: 204,
    errors: [],
  },
  'auth.logoutAll': {
    method: 'POST',
    path: '/api/auth/logout-all',
    access: 'session',
    operations: ['auth.session'],
    status: 204,
    errors: [],
  },
  // 039 (FR-023): свои действующие сеансы со способом входа и срок по ролям; сеанс по ссылке
  // видит только свои сеансы по ссылке (как «Выйти на всех устройствах», 030).
  'auth.sessions': {
    method: 'GET',
    path: '/api/auth/sessions',
    access: 'session',
    operations: ['auth.session'],
    status: 200,
    response: OwnSessionsSchema,
    errors: [],
  },
  'auth.me': {
    method: 'GET',
    path: '/api/auth/me',
    access: 'session',
    operations: ['profile.read'],
    status: 200,
    response: MeResponseSchema,
    errors: ['AUTH-004'],
  },
  // 031 (FR-028): пояс браузера при первом входе — только свой, только если его нет.
  'auth.timeZone': {
    method: 'POST',
    path: '/api/auth/time-zone',
    access: 'session',
    operations: ['auth.session'],
    body: TimeZoneBodySchema,
    status: 204,
    errors: ['REC-007'],
  },
  'auth.changePassword': {
    method: 'POST',
    path: '/api/auth/password',
    access: 'session',
    operations: ['profile.change_password'],
    body: ChangePasswordBodySchema,
    status: 204,
    errors: ['AUTH-002', 'AUTH-007', 'AUTH-008'],
  },
  // 025: восстановление пароля — открытый, ответ одинаков для любой почты (research Р1).
  'auth.recoverPassword': {
    method: 'POST',
    path: '/api/auth/password-recovery',
    access: 'public',
    operations: [],
    body: PasswordRecoveryBodySchema,
    status: 202,
    response: PasswordRecoveryResponseSchema,
    errors: [],
  },
  // 030: «Войти по ссылке на почту» и «Войти по коду из письма» — открытые, как восстановление
  // пароля: ответ на запрос ссылки одинаков для любой почты (research Р4; ADR-0044).
  'auth.requestLoginLink': {
    method: 'POST',
    path: '/api/auth/login-link',
    access: 'public',
    operations: [],
    body: RequestLoginLinkBodySchema,
    status: 202,
    response: LoginLinkRequestedSchema,
    errors: ['REC-007'],
  },
  'auth.loginByCode': {
    method: 'POST',
    path: '/api/auth/login-code',
    access: 'public',
    operations: [],
    body: LoginByCodeBodySchema,
    status: 200,
    response: LinkLoginResponseSchema,
    errors: ['AUTH-002', 'AUTH-011'],
  },
  // 030: «Помощь» пользователя по ссылке и ответственного (FR-032).
  'auth.help': {
    method: 'POST',
    path: '/api/help',
    access: 'session',
    operations: ['items.own'],
    body: HelpBodySchema,
    status: 204,
    errors: ['ACC-008'],
  },

  // Ссылки-приглашения
  'invites.get': {
    method: 'GET',
    path: '/api/invites/{token}',
    access: 'invite',
    operations: [],
    params: InviteTokenParamsSchema,
    status: 200,
    response: InviteInfoSchema,
    errors: ['AUTH-005', 'AUTH-006', 'AUTH-009', 'AUTH-012'],
  },
  'invites.accept': {
    method: 'POST',
    path: '/api/invites/{token}/accept',
    access: 'invite',
    operations: [],
    params: InviteTokenParamsSchema,
    body: AcceptInviteBodySchema,
    status: 204,
    errors: ['AUTH-005', 'AUTH-006', 'AUTH-007', 'AUTH-009', 'AUTH-012'],
  },

  // Ссылки входа (030: contracts/api.md, «Вход по ссылке»; research Р1, Р4; ADR-0044): как
  // ссылки-приглашения — токен в адресе, без сеанса; открытие страницы ссылку не тратит.
  'loginLinks.get': {
    method: 'GET',
    path: '/api/login-links/{token}',
    access: 'invite',
    operations: [],
    params: LoginLinkTokenParamsSchema,
    status: 200,
    response: LoginLinkInfoSchema,
    errors: [],
  },
  'loginLinks.exchange': {
    method: 'POST',
    path: '/api/login-links/{token}/exchange',
    access: 'invite',
    operations: [],
    params: LoginLinkTokenParamsSchema,
    body: EmptyBodySchema,
    status: 200,
    response: LinkLoginResponseSchema,
    errors: ['AUTH-005', 'AUTH-010'],
  },
  'loginLinks.notMe': {
    method: 'POST',
    path: '/api/login-links/{token}/not-me',
    access: 'invite',
    operations: [],
    params: LoginLinkTokenParamsSchema,
    body: EmptyBodySchema,
    status: 204,
    errors: ['AUTH-005', 'AUTH-010'],
  },
  'loginLinks.resend': {
    method: 'POST',
    path: '/api/login-links/{token}/resend',
    access: 'invite',
    operations: [],
    params: LoginLinkTokenParamsSchema,
    body: EmptyBodySchema,
    status: 202,
    response: LoginLinkResendResponseSchema,
    errors: ['AUTH-010'],
  },

  // Пользователи
  'users.list': {
    method: 'GET',
    path: '/api/users',
    access: 'session',
    operations: ['users.read'],
    query: UsersQuerySchema,
    status: 200,
    response: UsersPageSchema,
    errors: ['ACC-001'],
  },
  'users.create': {
    method: 'POST',
    path: '/api/users',
    access: 'session',
    operations: ['users.create'],
    body: CreateUserBodySchema,
    status: 201,
    response: CreateUserResponseSchema,
    errors: ['REC-003', 'REC-004', 'REC-007'],
  },
  'users.get': {
    method: 'GET',
    path: '/api/users/{id}',
    access: 'session',
    operations: ['users.read'],
    params: IdParamsSchema,
    status: 200,
    response: UserDetailSchema,
    errors: ['REC-002'],
  },
  'users.update': {
    method: 'PATCH',
    path: '/api/users/{id}',
    access: 'session',
    operations: ['users.update'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateUserBodySchema,
    status: 200,
    response: UserSchema,
    errors: ['REC-001', 'REC-003', 'REC-004', 'REC-009'],
  },
  // 041 (FR-006; research Р8): протоколы уходящего — новому владельцу (по умолчанию — тому,
  // кто переносит). Внешний, в архиве, заблокирован, сам уходящий — REC-007 поля ownerTo.
  'users.archive': {
    method: 'POST',
    path: '/api/users/{id}/archive',
    access: 'session',
    operations: ['users.archive'],
    params: IdParamsSchema,
    body: ArchiveUserBodySchema,
    status: 200,
    response: UserSchema,
    errors: ['ACC-004', 'REC-007'],
  },
  'users.restore': {
    method: 'POST',
    path: '/api/users/{id}/restore',
    access: 'session',
    operations: ['users.restore'],
    params: IdParamsSchema,
    status: 200,
    response: UserWithAssignmentsSchema,
    errors: [],
  },
  'users.invite': {
    method: 'POST',
    path: '/api/users/{id}/invite',
    access: 'session',
    operations: ['users.invite'],
    params: IdParamsSchema,
    status: 200,
    response: IssuedInviteSchema,
    errors: ['REC-009'],
  },
  // 035 (contracts/api.md 035): последнее приветственное письмо пользователя — окно после создания
  // и карточка; «Отправить письмо заново» — новое письмо (событие user.welcome_requested).
  'users.welcome': {
    method: 'GET',
    path: '/api/users/{id}/welcome',
    access: 'session',
    operations: ['users.read'],
    params: IdParamsSchema,
    status: 200,
    response: WelcomeLetterResponseSchema,
    errors: ['REC-002'],
  },
  'users.sendWelcome': {
    method: 'POST',
    path: '/api/users/{id}/welcome',
    access: 'session',
    operations: ['users.invite'],
    params: IdParamsSchema,
    status: 202,
    response: SendWelcomeResponseSchema,
    errors: ['REC-002', 'REC-009'],
  },
  'users.resetPassword': {
    method: 'POST',
    path: '/api/users/{id}/reset-password',
    access: 'session',
    operations: ['users.reset_password'],
    params: IdParamsSchema,
    status: 200,
    response: IssuedInviteSchema,
    errors: ['REC-009', 'ACC-004'],
  },
  'users.unlock': {
    method: 'POST',
    path: '/api/users/{id}/unlock',
    access: 'session',
    operations: ['users.unlock'],
    params: IdParamsSchema,
    status: 200,
    response: UserSchema,
    errors: ['REC-009'],
  },
  // «Отменить» в тосте «Блокировка снята» (T134): блокировка до прежнего срока; If-Match — версия
  // из ответа снятия, после него запись не меняли.
  'users.unlockUndo': {
    method: 'POST',
    path: '/api/users/{id}/unlock/undo',
    access: 'session',
    operations: ['users.unlock'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    status: 200,
    response: UserSchema,
    errors: ['REC-001', 'REC-009'],
  },
  'users.revokeSessions': {
    method: 'POST',
    path: '/api/users/{id}/sessions/revoke',
    access: 'session',
    operations: ['users.revoke_sessions'],
    params: IdParamsSchema,
    status: 204,
    errors: [],
  },

  // Назначения ролей
  'assignments.listForUser': {
    method: 'GET',
    path: '/api/users/{id}/assignments',
    access: 'session',
    operations: ['assignments.read'],
    params: IdParamsSchema,
    status: 200,
    response: AssignmentListSchema,
    errors: [],
  },
  'assignments.create': {
    method: 'POST',
    path: '/api/assignments',
    access: 'session',
    operations: ['assignments.manage'],
    body: CreateAssignmentBodySchema,
    status: 201,
    response: AssignmentSchema,
    errors: ['REC-007', 'REC-009', 'ACC-007'],
  },
  'assignments.update': {
    method: 'PATCH',
    path: '/api/assignments/{id}',
    access: 'session',
    operations: ['assignments.manage'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateAssignmentBodySchema,
    status: 200,
    response: AssignmentSchema,
    // Роли пользователя в архиве не правят, не снимают и не возвращают — REC-009 (FR-026в).
    // Снятую роль не правят — REC-001 с записью role.removed (T125).
    errors: ['REC-001', 'REC-007', 'REC-009', 'ACC-004'],
  },
  'assignments.archive': {
    method: 'POST',
    path: '/api/assignments/{id}/archive',
    access: 'session',
    operations: ['assignments.manage'],
    params: IdParamsSchema,
    // Версия, которую видел пользователь: изменили после открытия — REC-001 (T144).
    headers: AssignmentVersionHeadersSchema,
    status: 200,
    response: AssignmentSchema,
    errors: ['REC-001', 'REC-009', 'ACC-004'],
  },
  'assignments.restore': {
    method: 'POST',
    path: '/api/assignments/{id}/restore',
    access: 'session',
    operations: ['assignments.manage'],
    params: IdParamsSchema,
    headers: AssignmentVersionHeadersSchema,
    status: 200,
    response: AssignmentSchema,
    errors: ['REC-001', 'REC-009', 'ACC-007'],
  },

  // Права
  'rights.get': {
    method: 'GET',
    path: '/api/rights',
    access: 'session',
    operations: ['rights.read'],
    status: 200,
    response: RightsResponseSchema,
    errors: [],
  },

  // Доступ сопровождения
  'supportAccess.list': {
    method: 'GET',
    path: '/api/support-access',
    access: 'session',
    operations: ['support.read'],
    query: SupportGrantsQuerySchema,
    status: 200,
    response: SupportGrantsPageSchema,
    errors: [],
  },
  'supportAccess.grant': {
    method: 'POST',
    path: '/api/support-access',
    access: 'session',
    operations: ['support.grant'],
    body: CreateSupportGrantBodySchema,
    status: 201,
    response: SupportGrantSchema,
    errors: ['REC-007'],
  },
  'supportAccess.revoke': {
    method: 'POST',
    path: '/api/support-access/{id}/revoke',
    access: 'session',
    operations: ['support.revoke'],
    params: IdParamsSchema,
    status: 200,
    response: SupportGrantSchema,
    errors: [],
  },

  // Журнал действий
  'audit.list': {
    method: 'GET',
    path: '/api/audit',
    access: 'session',
    operations: ['audit.read.content', 'audit.read.access'],
    query: AuditListQuerySchema,
    status: 200,
    response: AuditPageSchema,
    errors: ['ACC-003'],
  },
  'audit.export': {
    method: 'GET',
    path: '/api/audit/export',
    access: 'session',
    operations: ['audit.export'],
    query: AuditExportQuerySchema,
    status: 200,
    file: 'xlsx',
    errors: ['ACC-003', 'REC-008'],
  },

  // Уведомления
  'notifications.list': {
    method: 'GET',
    path: '/api/notifications',
    access: 'session',
    operations: ['notifications.read'],
    query: NotificationsQuerySchema,
    status: 200,
    response: NotificationsResponseSchema,
    errors: [],
  },
  'notifications.read': {
    method: 'POST',
    path: '/api/notifications/{id}/read',
    access: 'session',
    operations: ['notifications.read'],
    params: IdParamsSchema,
    status: 204,
    errors: [],
  },
  // 0.26.1 (ACC-06): «Отметить разобранным» строку «требует действия»; повтор — без изменений.
  'notifications.resolve': {
    method: 'POST',
    path: '/api/notifications/{id}/resolve',
    access: 'session',
    operations: ['notifications.read'],
    params: IdParamsSchema,
    status: 204,
    errors: [],
  },
  // 0.26.1 (ACC-06, ревью W1): «Отменить» в тосте после «Отметить разобранным» — снимает только
  // отметку «закрыто»; «внесено» и «вопрос снят» не снимаются; повтор — без изменений.
  'notifications.unresolve': {
    method: 'POST',
    path: '/api/notifications/{id}/unresolve',
    access: 'session',
    operations: ['notifications.read'],
    params: IdParamsSchema,
    status: 204,
    errors: [],
  },
  'notifications.readAll': {
    method: 'POST',
    path: '/api/notifications/read-all',
    access: 'session',
    operations: ['notifications.read'],
    status: 204,
    errors: [],
  },

  // Выкатки
  'ops.deployments': {
    method: 'GET',
    path: '/api/ops/deployments',
    access: 'session',
    operations: ['ops.read'],
    status: 200,
    response: DeploymentsResponseSchema,
    errors: [],
  },

  // Выгрузка журнала в фоне и «Мои выгрузки» (002). Список, файл и удаление — только свои заказы.
  'audit.exports.create': {
    method: 'POST',
    path: '/api/audit/exports',
    access: 'session',
    operations: ['audit.export'],
    body: CreateAuditExportBodySchema,
    status: 201,
    response: AuditExportSchema,
    errors: ['ACC-003', 'REC-012', 'REC-013'],
  },
  'audit.exports.list': {
    method: 'GET',
    path: '/api/audit/exports',
    access: 'session',
    operations: ['audit.export'],
    query: AuditExportsQuerySchema,
    status: 200,
    response: AuditExportsPageSchema,
    // Сопровождение без временного доступа — ACC-003, как у журнала.
    errors: ['ACC-003'],
  },
  'audit.exports.file': {
    method: 'GET',
    path: '/api/audit/exports/{id}/file',
    access: 'session',
    operations: ['audit.export'],
    params: IdParamsSchema,
    status: 200,
    file: 'xlsx',
    // Чужой заказ или нет права в момент скачивания — ACC-001 и запись denied (FR-054).
    // Удалённый заказавшим — REC-002, по сроку или при архиве пользователя — REC-010 (FR-055а).
    errors: ['ACC-001', 'ACC-003', 'REC-002', 'REC-010', 'REC-011'],
  },
  'audit.exports.delete': {
    method: 'POST',
    path: '/api/audit/exports/{id}/delete',
    access: 'session',
    operations: ['audit.export'],
    params: IdParamsSchema,
    status: 200,
    response: AuditExportSchema,
    // Готовящийся заказ не удаляется: REC-011 (data-model.md, переходы заказа).
    errors: ['ACC-001', 'ACC-003', 'REC-011'],
  },

  // Сопровождение (002): состояние, фоновые задачи, технический журнал, оповещения, копии, метрики.
  'ops.health': {
    method: 'GET',
    path: '/api/ops/health',
    access: 'session',
    operations: ['ops.health.read'],
    status: 200,
    response: HealthBriefResponseSchema,
    errors: [],
  },
  'ops.status': {
    method: 'GET',
    path: '/api/ops/status',
    access: 'session',
    operations: ['ops.status.read'],
    status: 200,
    response: StatusResponseSchema,
    errors: [],
  },
  'ops.jobs.list': {
    method: 'GET',
    path: '/api/ops/jobs',
    access: 'session',
    operations: ['ops.jobs.read'],
    query: JobsQuerySchema,
    status: 200,
    response: JobsPageSchema,
    errors: [],
  },
  'ops.jobs.get': {
    method: 'GET',
    path: '/api/ops/jobs/{id}',
    access: 'session',
    operations: ['ops.jobs.read'],
    params: IdParamsSchema,
    status: 200,
    response: JobDetailSchema,
    errors: ['REC-002'],
  },
  'ops.jobs.retry': {
    method: 'POST',
    path: '/api/ops/jobs/{id}/retry',
    access: 'session',
    operations: ['ops.jobs.retry'],
    params: IdParamsSchema,
    status: 200,
    response: JobSchema,
    // Задачи нет или номер неверный — REC-002; не упала, в том числе выполняется, — OPS-002.
    errors: ['REC-002', 'OPS-002'],
  },
  // «Отменить» в тосте после «Повторить» — то же право, что повтор.
  'ops.jobs.undoRetry': {
    method: 'POST',
    path: '/api/ops/jobs/{id}/retry/undo',
    access: 'session',
    operations: ['ops.jobs.retry'],
    params: IdParamsSchema,
    status: 200,
    response: JobSchema,
    // Задачи нет или номер неверный — REC-002; обработчик уже взял задачу — OPS-004.
    errors: ['REC-002', 'OPS-004'],
  },
  'ops.jobs.cancel': {
    method: 'POST',
    path: '/api/ops/jobs/{id}/cancel',
    access: 'session',
    operations: ['ops.jobs.cancel'],
    params: IdParamsSchema,
    body: CancelJobBodySchema,
    status: 200,
    response: JobSchema,
    // Задачи нет или номер неверный — REC-002; неверное состояние в теле — REC-007.
    errors: ['REC-002', 'REC-007', 'OPS-001', 'OPS-002'],
  },
  'ops.log': {
    method: 'GET',
    path: '/api/ops/log',
    access: 'session',
    operations: ['ops.log.read'],
    query: LogQuerySchema,
    status: 200,
    response: LogPageSchema,
    // Период больше срока хранения или конец раньше начала.
    errors: ['REC-007'],
  },
  'ops.alerts': {
    method: 'GET',
    path: '/api/ops/alerts',
    access: 'session',
    operations: ['ops.alerts.read'],
    query: AlertsQuerySchema,
    status: 200,
    response: AlertsPageSchema,
    // Испорченный курсор.
    errors: ['REC-007'],
  },
  'ops.backups': {
    method: 'GET',
    path: '/api/ops/backups',
    access: 'session',
    operations: ['ops.backups.read'],
    query: BackupsQuerySchema,
    status: 200,
    response: BackupsResponseSchema,
    // Испорченный курсор или конец периода раньше начала.
    errors: ['REC-007'],
  },
  'ops.metrics': {
    method: 'GET',
    path: '/api/ops/metrics',
    access: 'session',
    operations: ['ops.metrics.read'],
    status: 200,
    file: 'text',
    errors: [],
  },

  // Документы и контроль (003, contracts/api.md, «Маршруты»). Права на запись документа — по его
  // области document:<id>; ведущий видит и загружает только документы своих областей.
  'manager.list': {
    method: 'GET',
    path: '/api/document-manager',
    access: 'session',
    operations: ['documents.read'],
    status: 200,
    response: DocumentManagerListSchema,
    errors: [],
  },
  'manager.folders.create': {
    method: 'POST',
    path: '/api/document-manager/folders',
    access: 'session',
    operations: ['documents.create'],
    body: CreateManagerFolderBodySchema,
    status: 201,
    response: DocumentManagerNodeSchema,
    errors: ['MGR-001', 'MGR-002', 'MGR-003'],
  },
  'manager.update': {
    method: 'PATCH',
    path: '/api/document-manager/nodes/{id}',
    access: 'session',
    operations: ['documents.manage', 'documents.create'],
    params: IdParamsSchema,
    body: UpdateManagerNodeBodySchema,
    status: 200,
    response: DocumentManagerNodeSchema,
    errors: ['REC-001', 'MGR-001', 'MGR-003'],
  },
  'manager.favorite': {
    method: 'POST',
    path: '/api/document-manager/nodes/{id}/favorite',
    access: 'session',
    operations: ['documents.read'],
    params: IdParamsSchema,
    body: ManagerFavoriteBodySchema,
    status: 200,
    response: DocumentManagerNodeSchema,
    errors: ['MGR-003'],
  },
  'manager.batch': {
    method: 'POST',
    path: '/api/document-manager/batch',
    access: 'session',
    operations: ['documents.manage', 'documents.create'],
    body: ManagerBatchBodySchema,
    status: 200,
    response: DocumentManagerListSchema,
    errors: ['REC-001', 'MGR-001', 'MGR-002', 'MGR-003'],
  },
  'manager.activity': {
    method: 'GET',
    path: '/api/document-manager/nodes/{id}/activity',
    access: 'session',
    operations: ['documents.read'],
    params: IdParamsSchema,
    status: 200,
    response: ManagerActivitySchema,
    errors: [],
  },
  'manager.comments.create': {
    method: 'POST',
    path: '/api/document-manager/nodes/{id}/comments',
    access: 'session',
    operations: ['documents.read'],
    params: IdParamsSchema,
    body: CreateManagerCommentBodySchema,
    status: 201,
    response: ManagerActivitySchema,
    errors: ['MGR-003'],
  },
  'manager.comments.update': {
    method: 'PATCH',
    path: '/api/document-manager/nodes/{id}/comments/{commentId}',
    access: 'session',
    operations: ['documents.read'],
    params: ManagerCommentParamsSchema,
    body: UpdateManagerCommentBodySchema,
    status: 200,
    response: ManagerActivitySchema,
    errors: ['REC-001', 'MGR-003'],
  },
  'manager.comments.delete': {
    method: 'DELETE',
    path: '/api/document-manager/nodes/{id}/comments/{commentId}',
    access: 'session',
    operations: ['documents.read'],
    params: ManagerCommentParamsSchema,
    body: DeleteManagerCommentBodySchema,
    status: 200,
    response: ManagerActivitySchema,
    errors: ['REC-001', 'MGR-003'],
  },
  'documents.list': {
    method: 'GET',
    path: '/api/documents',
    access: 'session',
    operations: ['documents.read'],
    status: 200,
    response: DocumentListSchema,
    errors: ['ACC-003'],
  },
  'documents.preview': {
    method: 'POST',
    path: '/api/documents/preview',
    access: 'session',
    operations: ['documents.create', 'documents.versions.upload'],
    upload: 'xlsx',
    query: PreviewQuerySchema,
    status: 200,
    response: PreviewResponseSchema,
    // Сверх api.md 003: «Новая версия» документа, которого нет или он вне областей; 011 —
    // «Новая версия» протокола в режиме ведения (DOC-006); 014 — протокола без файла (MIN-009).
    errors: [
      'DOC-001',
      'DOC-002',
      'DOC-003',
      'DOC-006',
      'ACC-001',
      'ACC-002',
      'REC-002',
      'MIN-009',
    ],
  },
  'documents.create': {
    method: 'POST',
    path: '/api/documents',
    access: 'session',
    operations: ['documents.create'],
    upload: 'xlsx',
    headers: UploadHeadersSchema,
    query: UploadQuerySchema,
    status: 201,
    response: DocumentRowSchema,
    errors: ['DOC-001', 'DOC-002', 'DOC-003', 'DOC-005', 'MGR-001', 'MGR-002', 'MGR-003'],
  },
  'documents.versions.create': {
    method: 'POST',
    path: '/api/documents/{id}/versions',
    access: 'session',
    operations: ['documents.versions.upload'],
    params: IdParamsSchema,
    upload: 'xlsx',
    headers: UploadHeadersSchema,
    query: UploadQuerySchema,
    status: 201,
    response: DocumentRowSchema,
    errors: [
      'DOC-001',
      'DOC-002',
      'DOC-003',
      'DOC-004',
      'DOC-005',
      'DOC-006',
      'ACC-001',
      'REC-002',
      'MIN-009',
    ],
  },
  'documents.versions.original': {
    method: 'GET',
    path: '/api/documents/{id}/versions/{versionId}/original',
    access: 'session',
    operations: ['documents.read'],
    params: VersionParamsSchema,
    status: 200,
    file: 'xlsx',
    errors: ['ACC-001', 'ACC-003', 'REC-002'],
  },
  // Боковая панель документа (приёмка 0.3.1): версии, новые сверху; права — как у оригинала.
  'documents.versions.list': {
    method: 'GET',
    path: '/api/documents/{id}/versions',
    access: 'session',
    operations: ['documents.read'],
    params: IdParamsSchema,
    status: 200,
    response: DocumentVersionListSchema,
    errors: ['ACC-001', 'ACC-003', 'REC-002'],
  },
  'documents.controlSheet': {
    method: 'GET',
    path: '/api/documents/{id}/control-sheet',
    access: 'session',
    operations: ['documents.read'],
    params: IdParamsSchema,
    query: ControlSheetQuerySchema,
    status: 200,
    file: 'html',
    errors: ['ACC-001', 'ACC-003', 'REC-002', 'ING-001'],
  },
  'control.items': {
    method: 'GET',
    path: '/api/control/items',
    access: 'session',
    operations: ['control.read'],
    query: ControlItemsQuerySchema,
    status: 200,
    response: ControlItemsResponseSchema,
    errors: ['ACC-003'],
  },

  // «Мои задачи» (030: contracts/api.md, «Мои задачи»; research Р3, Р6, Р8): право items.own —
  // связь учётной записи с человеком, без связи — ACC-008; чужой пункт, режим файла, пункт
  // компании — MIN-023.
  'my.items': {
    method: 'GET',
    path: '/api/my/items',
    access: 'session',
    operations: ['items.own'],
    status: 200,
    response: MyItemsSchema,
    errors: ['ACC-008'],
  },
  'my.summary': {
    method: 'GET',
    path: '/api/my/summary',
    access: 'session',
    operations: ['items.own'],
    status: 200,
    response: MySummarySchema,
    errors: ['ACC-008'],
  },
  'my.answer': {
    method: 'POST',
    path: '/api/my/items/{itemId}/answer',
    access: 'session',
    operations: ['items.own'],
    params: MyItemParamsSchema,
    body: MyAnswerSchema,
    status: 200,
    response: MyAnswerResultSchema,
    errors: ['ACC-008', 'REC-001', 'REC-002', 'MIN-004', 'MIN-023'],
  },
  'my.undo': {
    method: 'POST',
    path: '/api/my/answers/{commandId}/undo',
    access: 'session',
    operations: ['items.own'],
    params: MyCommandParamsSchema,
    body: EmptyBodySchema,
    status: 200,
    response: MyAnswerResultSchema,
    errors: ['ACC-008', 'REC-001', 'REC-002', 'MIN-007', 'MIN-023'],
  },
  'my.report': {
    method: 'POST',
    path: '/api/my/items/{itemId}/report',
    access: 'session',
    operations: ['items.own'],
    params: MyItemParamsSchema,
    body: MyReportSchema,
    status: 204,
    errors: ['ACC-008', 'REC-002', 'MIN-023'],
  },
  'my.seen': {
    method: 'POST',
    path: '/api/my/seen',
    access: 'session',
    operations: ['items.own'],
    body: MySeenBodySchema,
    status: 204,
    errors: ['ACC-008'],
  },
  'my.banner': {
    method: 'POST',
    path: '/api/my/banner/close',
    access: 'session',
    operations: ['items.own'],
    body: EmptyBodySchema,
    status: 204,
    errors: ['ACC-008'],
  },
  'my.history': {
    method: 'GET',
    path: '/api/my/items/{itemId}/history',
    access: 'session',
    operations: ['items.own'],
    params: MyItemParamsSchema,
    query: ItemHistoryQuerySchema,
    status: 200,
    response: ItemHistoryResponseSchema,
    errors: ['ACC-008', 'REC-002', 'MIN-023'],
  },
  'my.section': {
    method: 'GET',
    path: '/api/my/items/{itemId}/section',
    access: 'session',
    operations: ['items.own'],
    params: MyItemParamsSchema,
    status: 200,
    response: MySectionSchema,
    errors: ['ACC-008', 'REC-002', 'MIN-023'],
  },
  'my.people': {
    method: 'GET',
    path: '/api/my/people',
    access: 'session',
    operations: ['items.own'],
    query: MyPeopleQuerySchema,
    status: 200,
    response: MyPeopleResponseSchema,
    errors: ['ACC-001', 'ACC-008'],
  },

  // Протокол в системе (011, contracts/api.md, «Маршруты»; ADR-0031, ADR-0032). Перевод и возврат —
  // по области документа document:<id>; команды таблицы — одна точка записи с ревизией.
  'minutes.transfer.preview': {
    method: 'GET',
    path: '/api/documents/{id}/transfer',
    access: 'session',
    operations: ['minutes.transfer'],
    params: IdParamsSchema,
    status: 200,
    response: TransferPreviewSchema,
    errors: ['REC-002', 'MIN-002', 'MIN-003'],
  },
  'minutes.transfer': {
    method: 'POST',
    path: '/api/documents/{id}/transfer',
    access: 'session',
    operations: ['minutes.transfer'],
    params: IdParamsSchema,
    body: TransferBodySchema,
    status: 201,
    response: CommandAcceptedSchema,
    errors: ['REC-002', 'REC-007', 'MIN-002', 'MIN-003', 'MIN-004', 'MIN-005'],
  },
  'minutes.revert': {
    method: 'POST',
    path: '/api/documents/{id}/revert',
    access: 'session',
    operations: ['minutes.transfer'],
    params: IdParamsSchema,
    body: RevertBodySchema,
    status: 200,
    response: CommandAcceptedSchema,
    errors: ['REC-001', 'REC-002', 'REC-007', 'MIN-001', 'MIN-004', 'MIN-009'],
  },
  // 014: путь без параметра — раньше /api/protocols/{id}: подмены API в тестах сверяют пути
  // по порядку таблицы (настоящий сервер и так предпочитает неизменяемый путь).
  // 040 (ревью): список пользователей для выбора владельца при создании — только администратору.
  'minutes.leads.candidates': {
    method: 'GET',
    path: '/api/protocols/lead-candidates',
    access: 'session',
    operations: ['assignments.manage'],
    query: LeadCandidatesQuerySchema,
    status: 200,
    response: LeadCandidatesResponseSchema,
    errors: ['REC-007'],
  },
  // 038: окно «Назначить ведущего» (contracts/api.md 038, «Маршруты»; research Р1, Р5, Р6, Р8).
  // Шлюз — право назначать (documents.create) или создавать пользователей (users.create);
  // обработчик ещё проверяет minutes.edit по области протокола. Пути глубже /api/protocols/{id}
  // — раньше него в таблице (подмены API в тестах сверяют пути по порядку).
  'minutes.lead.lookup': {
    method: 'GET',
    path: '/api/protocols/{id}/lead/lookup',
    access: 'session',
    operations: ['documents.create'],
    params: IdParamsSchema,
    query: LeadLookupQuerySchema,
    status: 200,
    response: LeadLookupResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001'],
  },
  'minutes.lead.get': {
    method: 'GET',
    path: '/api/protocols/{id}/lead',
    access: 'session',
    operations: ['documents.create'],
    params: IdParamsSchema,
    status: 200,
    response: LeadStateSchema,
    errors: ['ACC-005', 'REC-002', 'MIN-001'],
  },
  'minutes.lead.create': {
    method: 'POST',
    path: '/api/protocols/{id}/lead/user',
    access: 'session',
    operations: ['users.create'],
    params: IdParamsSchema,
    body: CreateLeadBodySchema,
    status: 201,
    response: CreateLeadResponseSchema,
    errors: [
      'ACC-005',
      'ACC-007',
      'REC-002',
      'REC-003',
      'REC-004',
      'REC-007',
      'MIN-001',
      'MIN-025',
      'MIN-026',
    ],
  },
  'minutes.state': {
    method: 'GET',
    path: '/api/protocols/{id}',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    status: 200,
    response: ProtocolStateSchema,
    errors: ['ACC-005', 'ACC-003', 'REC-002', 'MIN-001'],
  },
  'minutes.pasteAnalysis.create': {
    method: 'POST',
    path: '/api/protocols/{id}/paste-analysis',
    access: 'session',
    operations: ['minutes.edit'],
    params: IdParamsSchema,
    body: PasteAnalysisCreateBodySchema,
    bodyLimit: PASTE_ANALYSIS_BODY_LIMIT,
    status: 202,
    response: PasteAnalysisReceiptSchema,
    errors: [
      'ACC-003',
      'REC-002',
      'REC-007',
      'MIN-001',
      'SPT-001',
      'SPT-002',
      'SPT-003',
      'SPT-004',
      'SPT-005',
      'SPT-006',
    ],
  },
  'minutes.pasteAnalysis.get': {
    method: 'GET',
    path: '/api/protocols/{id}/paste-analysis/{analysisId}',
    access: 'session',
    operations: ['minutes.edit'],
    params: PasteAnalysisParamsSchema,
    status: 200,
    response: PasteAnalysisGetSchema,
    errors: [
      'ACC-003',
      'REC-002',
      'REC-007',
      'MIN-001',
      'SPT-001',
      'SPT-002',
      'SPT-003',
      'SPT-004',
      'SPT-005',
      'SPT-006',
    ],
  },
  'minutes.pasteAnalysis.cancel': {
    method: 'DELETE',
    path: '/api/protocols/{id}/paste-analysis/{analysisId}',
    access: 'session',
    operations: ['minutes.edit'],
    params: PasteAnalysisParamsSchema,
    status: 200,
    response: PasteAnalysisGetSchema,
    errors: [
      'ACC-003',
      'REC-002',
      'REC-007',
      'MIN-001',
      'SPT-001',
      'SPT-002',
      'SPT-003',
      'SPT-004',
      'SPT-005',
      'SPT-006',
    ],
  },
  'minutes.pasteAnalysis.decisions': {
    method: 'PATCH',
    path: '/api/protocols/{id}/paste-analysis/{analysisId}/decisions',
    access: 'session',
    operations: ['minutes.edit'],
    params: PasteAnalysisParamsSchema,
    body: PasteFeedbackBodySchema,
    bodyLimit: COMMAND_BODY_LIMIT,
    status: 200,
    response: PasteFeedbackAcceptedSchema,
    errors: [
      'ACC-003',
      'REC-002',
      'REC-007',
      'MIN-001',
      'SPT-001',
      'SPT-002',
      'SPT-003',
      'SPT-004',
      'SPT-005',
      'SPT-006',
    ],
  },
  'minutes.commands': {
    method: 'POST',
    path: '/api/protocols/{id}/commands',
    access: 'session',
    // 030 (research Р2): правка своих пунктов — items.own, зона из записи человека; чужой пункт
    // среди затронутых — MIN-023 целиком.
    operations: ['minutes.edit', 'items.own'],
    params: IdParamsSchema,
    body: CommandSchema,
    bodyLimit: COMMAND_BODY_LIMIT,
    status: 200,
    response: CommandAcceptedSchema,
    errors: [
      'ACC-005',
      'REC-001',
      'REC-002',
      'REC-007',
      'MIN-001',
      'MIN-004',
      'MIN-005',
      'MIN-006',
      'MIN-007',
      'MIN-010',
      'SPT-006',
      'MIN-014',
      'MIN-015',
      // 0.20.1: parties.move меняет вид записи в справочнике — отказы смены вида.
      'MIN-019',
      // 029: version.restore и version.revert — протокол изменили, менять нечего, версии нет.
      'MIN-020',
      'MIN-021',
      'MIN-022',
      // 030: команда «своих пунктов» с чужим пунктом.
      'MIN-023',
      'DIR-004',
      'DIR-007',
      'DIR-008',
      'DIR-010',
      'DIR-014',
    ],
  },
  // 012: личный вид таблицы — право чтения протокола; данные протокола не меняются (Р11).
  // POST, а не PATCH: у вида нет версии записи и If-Match, последняя запись выигрывает.
  'minutes.view.save': {
    method: 'POST',
    path: '/api/protocols/{id}/view',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    body: ProtocolViewPatchSchema,
    status: 200,
    response: ProtocolViewSchema,
    errors: ['ACC-005', 'ACC-003', 'REC-002', 'REC-007', 'MIN-001'],
  },
  // 014: протокол без файла, ведущий, поиск записей для поповера, история состояния
  // (contracts/api.md, «Маршруты»; research, Р1, Р4–Р6).
  'minutes.create': {
    method: 'POST',
    path: '/api/protocols',
    access: 'session',
    operations: ['documents.create'],
    body: CreateProtocolBodySchema,
    status: 201,
    response: CreatedProtocolSchema,
    errors: ['REC-007', 'MIN-010', 'MGR-001', 'MGR-002', 'MGR-003', 'ACC-007'],
  },
  'minutes.lead.set': {
    method: 'POST',
    path: '/api/protocols/{id}/lead',
    access: 'session',
    // Шлюз — право создавать протоколы; обработчик ещё проверяет minutes.edit по области.
    operations: ['documents.create'],
    params: IdParamsSchema,
    body: SetLeadBodySchema,
    status: 200,
    response: SetLeadResponseSchema,
    errors: ['REC-002', 'REC-007', 'MIN-001', 'MIN-010', 'MIN-025', 'ACC-007'],
  },
  'minutes.people.search': {
    method: 'GET',
    path: '/api/protocols/{id}/people',
    access: 'session',
    operations: ['minutes.edit'],
    params: IdParamsSchema,
    query: PeopleSearchQuerySchema,
    status: 200,
    response: PeopleSearchResponseSchema,
    errors: ['REC-002', 'REC-007', 'MIN-001'],
  },
  'minutes.assigneeOptions.search': {
    method: 'GET',
    path: '/api/protocols/{id}/assignee-options',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: AssigneeOptionsQuerySchema,
    status: 200,
    response: AssigneeOptionsResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001'],
  },
  // 027: вкладки «Ответственные» и «Контрагенты» (FR-012) и короткая форма поповера (FR-019).
  'minutes.parties.list': {
    method: 'GET',
    path: '/api/protocols/{id}/parties',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: PartiesListQuerySchema,
    status: 200,
    response: ProtocolPartiesResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001'],
  },
  'minutes.parties.create': {
    method: 'POST',
    path: '/api/protocols/{id}/parties',
    access: 'session',
    // directory.write в области протокола проверяет обработчик (027: research, Р4).
    operations: ['minutes.edit'],
    params: IdParamsSchema,
    body: PartyCreateBodySchema,
    status: 201,
    response: PartyCreatedSchema,
    errors: ['ACC-002', 'REC-002', 'REC-007', 'MIN-001', 'DIR-001', 'DIR-004'],
  },
  'minutes.item.history': {
    method: 'GET',
    path: '/api/protocols/{id}/items/{itemId}/history',
    access: 'session',
    operations: ['minutes.read'],
    params: ItemHistoryParamsSchema,
    query: ItemHistoryQuerySchema,
    status: 200,
    response: ItemHistoryResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001', 'MIN-005'],
  },
  // Совместная правка и история (015: contracts/api.md, «Маршруты»; research, Р1, Р3): изменения
  // после ревизии с присутствием — фоновый опрос раз в 2 секунды; лента «Изменения».
  'minutes.changes': {
    method: 'GET',
    path: '/api/protocols/{id}/changes',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: ChangesQuerySchema,
    status: 200,
    response: ProtocolChangesSchema,
    errors: ['ACC-005', 'ACC-003', 'REC-002', 'REC-007', 'MIN-001'],
  },
  'minutes.revisions': {
    method: 'GET',
    path: '/api/protocols/{id}/revisions',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: RevisionsQuerySchema,
    status: 200,
    response: ProtocolRevisionsSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001'],
  },
  // Снимок недели, печать, выгрузка (016: contracts/api.md, «Маршруты»; research Р1, Р4, Р6, Р7).
  // Фиксация — команда minutes.commands (week.snapshot); остальное — по праву чтения протокола.
  'minutes.snapshots.list': {
    method: 'GET',
    path: '/api/protocols/{id}/snapshots',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    status: 200,
    response: SnapshotListSchema,
    errors: ['ACC-005', 'REC-002', 'MIN-001'],
  },
  'minutes.snapshots.get': {
    method: 'GET',
    path: '/api/protocols/{id}/snapshots/{snapshotId}',
    access: 'session',
    operations: ['minutes.read'],
    params: SnapshotParamsSchema,
    status: 200,
    response: SnapshotViewSchema,
    errors: ['ACC-005', 'REC-002', 'MIN-001', 'MIN-011'],
  },
  'minutes.print': {
    method: 'GET',
    path: '/api/protocols/{id}/print',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: PrintOptionsSchema,
    status: 200,
    file: 'html',
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001', 'MIN-011', 'MIN-012'],
  },
  'minutes.export.xlsx': {
    method: 'GET',
    path: '/api/protocols/{id}/export.xlsx',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: ExportXlsxQuerySchema,
    status: 200,
    file: 'xlsx',
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001', 'MIN-011'],
  },
  'minutes.export.json': {
    method: 'GET',
    path: '/api/protocols/{id}/export.json',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    status: 200,
    file: 'json',
    errors: ['ACC-005', 'REC-002', 'MIN-001'],
  },
  // История версий (029: contracts/api.md, «Маршруты»; research Р1–Р5). Восстановление и возврат —
  // команды minutes.commands (version.restore, version.revert); здесь — чтение, имена и окна чисел.
  // Предпросмотр возврата — POST (выделение в теле), данных не меняет.
  'minutes.versions.list': {
    method: 'GET',
    path: '/api/protocols/{id}/versions',
    access: 'session',
    operations: ['minutes.read'],
    params: IdParamsSchema,
    query: VersionsQuerySchema,
    status: 200,
    response: VersionListSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001'],
  },
  'minutes.versions.get': {
    method: 'GET',
    path: '/api/protocols/{id}/versions/{revision}',
    access: 'session',
    operations: ['minutes.read'],
    params: ProtocolVersionParamsSchema,
    query: VersionViewQuerySchema,
    status: 200,
    response: VersionViewSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001', 'MIN-022'],
  },
  'minutes.versions.restorePreview': {
    method: 'GET',
    path: '/api/protocols/{id}/versions/{revision}/restore',
    access: 'session',
    operations: ['minutes.manage'],
    params: ProtocolVersionParamsSchema,
    status: 200,
    response: RestorePreviewSchema,
    errors: ['ACC-005', 'REC-002', 'MIN-001', 'MIN-022'],
  },
  'minutes.versions.revertPreview': {
    method: 'POST',
    path: '/api/protocols/{id}/versions/{revision}/revert-preview',
    access: 'session',
    operations: ['minutes.edit'],
    params: ProtocolVersionParamsSchema,
    body: VersionSelectionSchema,
    status: 200,
    response: RevertPreviewSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007', 'MIN-001', 'MIN-022'],
  },
  'minutes.versions.name': {
    method: 'PUT',
    path: '/api/protocols/{id}/versions/{revision}/name',
    access: 'session',
    operations: ['minutes.edit'],
    params: ProtocolVersionParamsSchema,
    body: VersionNameBodySchema,
    status: 200,
    response: VersionNameResponseSchema,
    errors: ['ACC-005', 'REC-001', 'REC-002', 'REC-007', 'MIN-001', 'MIN-022'],
  },
  'minutes.versions.unname': {
    method: 'DELETE',
    path: '/api/protocols/{id}/versions/{revision}/name',
    access: 'session',
    operations: ['minutes.edit'],
    params: ProtocolVersionParamsSchema,
    status: 204,
    errors: ['ACC-005', 'REC-002', 'MIN-001', 'MIN-022'],
  },
  // Доступ к протоколу (020: contracts/api.md, «Маршруты»; research, Р1–Р8). Принимают протокол
  // в любом режиме: доступ и общий доступ действуют и на протокол в режиме файла.
  'minutes.access.get': {
    method: 'GET',
    path: '/api/protocols/{id}/access',
    access: 'session',
    operations: ['minutes.access.read'],
    params: IdParamsSchema,
    status: 200,
    response: ProtocolAccessViewSchema,
    errors: ['ACC-005', 'REC-002'],
  },
  'minutes.access.candidates': {
    method: 'GET',
    path: '/api/protocols/{id}/access/candidates',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: IdParamsSchema,
    query: AccessCandidatesQuerySchema,
    status: 200,
    response: AccessCandidatesResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007'],
  },
  'minutes.access.zoneOptions': {
    method: 'GET',
    path: '/api/protocols/{id}/access/zone-options',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: IdParamsSchema,
    query: ZoneOptionsQuerySchema,
    status: 200,
    response: ZoneOptionsResponseSchema,
    errors: ['ACC-005', 'REC-002', 'REC-007'],
  },
  'minutes.access.grant': {
    method: 'POST',
    path: '/api/protocols/{id}/access',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: IdParamsSchema,
    body: GrantBodySchema,
    status: 201,
    response: ProtocolAccessViewSchema,
    errors: ['ACC-005', 'REC-002', 'REC-004', 'REC-007', 'MIN-016', 'MIN-018'],
  },
  'minutes.access.update': {
    method: 'PATCH',
    path: '/api/protocols/{id}/access/{grantId}',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: GrantParamsSchema,
    headers: IfMatchHeadersSchema,
    body: GrantUpdateBodySchema,
    status: 200,
    response: ProtocolAccessViewSchema,
    errors: ['ACC-005', 'REC-001', 'REC-002', 'REC-007', 'MIN-018'],
  },
  'minutes.access.remove': {
    method: 'POST',
    path: '/api/protocols/{id}/access/{grantId}/remove',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: GrantParamsSchema,
    headers: IfMatchHeadersSchema,
    status: 200,
    response: ProtocolAccessViewSchema,
    errors: ['ACC-005', 'REC-001', 'REC-002'],
  },
  // If-Match — версия общего доступа (sharingVersion); строки ещё нет — 0 (схема ниже).
  'minutes.access.sharing': {
    method: 'POST',
    path: '/api/protocols/{id}/sharing',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: IdParamsSchema,
    headers: SharingIfMatchHeadersSchema,
    body: SharingBodySchema,
    status: 200,
    response: ProtocolAccessViewSchema,
    errors: ['ACC-005', 'REC-001', 'REC-002', 'REC-007'],
  },
  'minutes.access.invite': {
    method: 'POST',
    path: '/api/protocols/{id}/access/invite',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: IdParamsSchema,
    body: InviteBodySchema,
    status: 201,
    response: InviteResponseSchema,
    errors: [
      'ACC-005',
      'ACC-006',
      'REC-002',
      'REC-004',
      'REC-007',
      'MIN-016',
      'MIN-017',
      'MIN-018',
    ],
  },
  'minutes.access.resend': {
    method: 'POST',
    path: '/api/protocols/{id}/access/{grantId}/invite',
    access: 'session',
    operations: ['minutes.access.manage'],
    params: GrantParamsSchema,
    status: 202,
    response: ResendResponseSchema,
    errors: ['ACC-005', 'ACC-006', 'REC-002'],
  },
  // 022: «Открытые вам протоколы» — главная внешнего (contracts/api.md 022, research Р11).
  // Операция «в сеансе» profile.read: любой вошедший, только протоколы с его ролью.
  'minutes.mine': {
    method: 'GET',
    path: '/api/protocols/mine',
    access: 'session',
    operations: ['profile.read'],
    status: 200,
    response: MineResponseSchema,
    errors: [],
  },
  // 023: доступ в «Документах» (contracts/api.md 023, research Р1–Р3). Операция маршрута — в какой-
  // нибудь области; право на каждый протокол проверяет обработчик.
  'minutes.access.summary': {
    method: 'GET',
    path: '/api/protocol-access/summary',
    access: 'session',
    operations: ['minutes.access.read'],
    status: 200,
    response: AccessSummaryResponseSchema,
    errors: [],
  },
  'minutes.access.bulk': {
    method: 'POST',
    path: '/api/protocol-access/bulk',
    access: 'session',
    operations: ['minutes.access.manage'],
    body: BulkAccessBodySchema,
    status: 200,
    response: BulkAccessResultSchema,
    errors: ['ACC-006', 'MIN-016', 'MIN-017', 'MIN-018', 'REC-002', 'REC-004', 'REC-007'],
  },
  'minutes.access.lookup': {
    method: 'GET',
    path: '/api/protocol-access/candidates',
    access: 'session',
    operations: ['minutes.access.manage'],
    query: AccessCandidatesQuerySchema,
    status: 200,
    response: AccessCandidatesResponseSchema,
    errors: ['REC-007'],
  },
  'minutes.access.lookupZones': {
    method: 'GET',
    path: '/api/protocol-access/zone-options',
    access: 'session',
    operations: ['minutes.access.manage'],
    query: ZoneOptionsQuerySchema,
    status: 200,
    response: ZoneOptionsResponseSchema,
    errors: ['REC-007'],
  },
  'minutes.access.byUser': {
    method: 'GET',
    path: '/api/users/{id}/protocol-access',
    access: 'session',
    operations: ['users.read'],
    params: IdParamsSchema,
    status: 200,
    response: UserProtocolAccessSchema,
    errors: ['REC-002'],
  },

  // Справочник (005, contracts/api.md, «Маршруты»; обработчики — дорожка S1). Права на запись —
  // по документам, где запись упомянута: ведущий видит и дополняет записи своих документов.
  'directory.records.list': {
    method: 'GET',
    path: '/api/directory/records',
    access: 'session',
    operations: ['directory.read'],
    query: RecordsQuerySchema,
    status: 200,
    response: RecordsPageSchema,
    errors: ['ACC-003'],
  },
  'directory.people.get': {
    method: 'GET',
    path: '/api/directory/people/{id}',
    access: 'session',
    operations: ['directory.read'],
    params: IdParamsSchema,
    status: 200,
    response: PersonCardSchema,
    errors: ['ACC-003'],
  },
  'directory.organizations.get': {
    method: 'GET',
    path: '/api/directory/organizations/{id}',
    access: 'session',
    operations: ['directory.read'],
    params: IdParamsSchema,
    status: 200,
    response: OrganizationCardSchema,
    errors: ['ACC-003'],
  },
  'directory.mentions.list': {
    method: 'GET',
    path: '/api/directory/mentions',
    access: 'session',
    operations: ['directory.read'],
    query: MentionsQuerySchema,
    status: 200,
    response: MentionsPageSchema,
    errors: ['ACC-003'],
  },
  'directory.pairs.list': {
    method: 'GET',
    path: '/api/directory/pairs',
    access: 'session',
    operations: ['directory.read'],
    query: PairsQuerySchema,
    status: 200,
    response: PairsPageSchema,
    errors: ['ACC-003'],
  },
  'directory.pairs.get': {
    method: 'GET',
    path: '/api/directory/pairs/{id}',
    access: 'session',
    operations: ['directory.read'],
    params: IdParamsSchema,
    status: 200,
    response: PairCardSchema,
    errors: ['ACC-003'],
  },
  'directory.triage': {
    method: 'GET',
    path: '/api/directory/triage',
    access: 'session',
    operations: ['directory.read'],
    status: 200,
    response: TriageSchema,
    errors: ['ACC-003'],
  },
  'directory.search': {
    method: 'GET',
    path: '/api/directory/search',
    access: 'session',
    operations: ['directory.read'],
    query: DirectorySearchQuerySchema,
    status: 200,
    response: DirectorySearchResponseSchema,
    errors: ['ACC-003'],
  },
  'directory.zones.list': {
    method: 'GET',
    path: '/api/directory/zones',
    access: 'session',
    operations: ['directory.read'],
    status: 200,
    response: ZoneListSchema,
    errors: ['ACC-003'],
  },
  'directory.versions.summary': {
    method: 'GET',
    path: '/api/directory/versions/{versionId}/summary',
    access: 'session',
    operations: ['directory.read'],
    params: VersionSummaryParamsSchema,
    status: 200,
    response: VersionDirectorySummarySchema,
    errors: ['ACC-002', 'ACC-003', 'REC-002'],
  },
  'directory.people.create': {
    method: 'POST',
    path: '/api/directory/people',
    access: 'session',
    operations: ['directory.write'],
    body: CreatePersonBodySchema,
    status: 201,
    response: PersonCardSchema,
    errors: ['DIR-004'],
  },
  'directory.organizations.create': {
    method: 'POST',
    path: '/api/directory/organizations',
    access: 'session',
    operations: ['directory.write'],
    body: CreateOrganizationBodySchema,
    status: 201,
    response: OrganizationCardSchema,
    errors: [],
  },
  'directory.people.update': {
    method: 'PATCH',
    path: '/api/directory/people/{id}',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdatePersonBodySchema,
    status: 200,
    response: PersonCardSchema,
    errors: ['REC-001', 'DIR-004'],
  },
  'directory.organizations.update': {
    method: 'PATCH',
    path: '/api/directory/organizations/{id}',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateOrganizationBodySchema,
    status: 200,
    response: OrganizationCardSchema,
    errors: ['REC-001', 'DIR-004'],
  },
  'directory.people.setEmail': {
    method: 'POST',
    path: '/api/directory/people/{id}/email',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    body: SetEmailBodySchema,
    status: 200,
    response: SetEmailResponseSchema,
    errors: ['DIR-001', 'DIR-004'],
  },
  // 031 (FR-013): пакетный ввод почты из окна готовности — правило одиночного ввода на строку.
  'directory.people.setEmails': {
    method: 'POST',
    path: '/api/directory/people/emails',
    access: 'session',
    operations: ['directory.write'],
    body: SetEmailsBodySchema,
    status: 200,
    response: SetEmailsResultSchema,
    errors: ['REC-007'],
  },
  'directory.emails.confirm': {
    method: 'POST',
    path: '/api/directory/emails/{id}/confirm',
    access: 'session',
    operations: ['directory.decide'],
    params: IdParamsSchema,
    status: 200,
    response: PersonCardSchema,
    errors: ['DIR-004'],
  },
  'directory.organizations.setReceivesMail': {
    method: 'POST',
    path: '/api/directory/organizations/{id}/receives-mail',
    access: 'session',
    operations: ['directory.decide'],
    params: IdParamsSchema,
    body: ReceivesMailBodySchema,
    status: 200,
    response: OrganizationCardSchema,
    errors: ['DIR-004'],
  },
  'directory.workplaces.create': {
    method: 'POST',
    path: '/api/directory/workplaces',
    access: 'session',
    operations: ['directory.write'],
    body: CreateWorkplaceBodySchema,
    status: 201,
    response: WorkplaceSchema,
    errors: ['DIR-004', 'DIR-013'],
  },
  'directory.workplaces.update': {
    method: 'PATCH',
    path: '/api/directory/workplaces/{id}',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateWorkplaceBodySchema,
    status: 200,
    response: WorkplaceSchema,
    errors: ['REC-001', 'DIR-013'],
  },
  'directory.workplaces.remove': {
    method: 'POST',
    path: '/api/directory/workplaces/{id}/remove',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    status: 204,
    errors: [],
  },
  'directory.curatorships.create': {
    method: 'POST',
    path: '/api/directory/curatorships',
    access: 'session',
    operations: ['directory.write'],
    body: CreateCuratorshipBodySchema,
    status: 201,
    response: CuratorshipSchema,
    errors: ['DIR-004', 'DIR-013'],
  },
  'directory.curatorships.setPrimary': {
    method: 'POST',
    path: '/api/directory/curatorships/{id}/primary',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    status: 200,
    response: CuratorshipSchema,
    errors: [],
  },
  'directory.curatorships.remove': {
    method: 'POST',
    path: '/api/directory/curatorships/{id}/remove',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    body: RemoveCuratorshipBodySchema,
    status: 204,
    errors: ['DIR-003'],
  },
  'directory.zones.set': {
    method: 'POST',
    path: '/api/directory/zones/{documentId}',
    access: 'session',
    operations: ['directory.write'],
    params: ZoneParamsSchema,
    body: SetZoneBodySchema,
    status: 200,
    response: ZoneSchema,
    errors: ['ACC-002', 'REC-002', 'DIR-004', 'DIR-013'],
  },
  'directory.aliases.create': {
    method: 'POST',
    path: '/api/directory/aliases',
    access: 'session',
    operations: ['directory.write'],
    body: CreateAliasBodySchema,
    status: 201,
    response: AliasSchema,
    errors: ['DIR-004'],
  },
  'directory.aliases.reject': {
    method: 'POST',
    path: '/api/directory/aliases/{id}/reject',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    status: 200,
    response: AliasRejectionSchema,
    errors: [],
  },
  // 0.22.5 (ASG-RES-13): «Отменить» после «Не он» — псевдоним и снятые связи как были.
  'directory.aliases.undoReject': {
    method: 'POST',
    path: '/api/directory/aliases/{id}/reject/undo',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    status: 200,
    response: AliasRejectionSchema,
    errors: ['DIR-004', 'DIR-017'],
  },
  // 030: запросы «Проверить связь упоминания» в «Вопросах» и решение по ним (research Р7).
  'directory.linkChecks.list': {
    method: 'GET',
    path: '/api/directory/link-checks',
    access: 'session',
    operations: ['directory.read'],
    query: LinkChecksQuerySchema,
    status: 200,
    response: LinkChecksResponseSchema,
    errors: [],
  },
  'directory.linkChecks.decide': {
    method: 'POST',
    path: '/api/directory/link-checks/{id}/decide',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    body: LinkCheckDecideBodySchema,
    status: 200,
    response: LinkCheckRowSchema,
    errors: ['REC-001', 'DIR-004', 'DIR-018'],
  },
  'directory.mentions.resolve': {
    method: 'POST',
    path: '/api/directory/mentions/{id}/resolve',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    body: ResolveMentionBodySchema,
    status: 200,
    response: MentionRowSchema,
    errors: ['DIR-004', 'DIR-013'],
  },
  'directory.pairs.create': {
    method: 'POST',
    path: '/api/directory/pairs',
    access: 'session',
    operations: ['directory.write'],
    body: CreatePairBodySchema,
    status: 201,
    response: PairSchema,
    errors: ['DIR-004'],
  },
  'directory.pairs.decide': {
    method: 'POST',
    path: '/api/directory/pairs/{id}/decision',
    access: 'session',
    operations: ['directory.decide'],
    params: IdParamsSchema,
    body: PairDecisionBodySchema,
    status: 200,
    response: PairDecisionResultSchema,
    errors: ['DIR-004', 'DIR-005', 'DIR-014'],
  },
  'directory.merges.undo': {
    method: 'POST',
    path: '/api/directory/merges/{id}/undo',
    access: 'session',
    operations: ['directory.decide'],
    params: IdParamsSchema,
    status: 200,
    response: MergeSchema,
    errors: ['DIR-006'],
  },

  // 005-2: смена вида записи и вопросы о виде (contracts/api.md 005-2, «Маршруты»).
  'directory.kindChanges.preview': {
    method: 'GET',
    path: '/api/directory/kind-changes/preview',
    access: 'session',
    operations: ['directory.write'],
    query: KindChangePreviewQuerySchema,
    status: 200,
    response: KindChangePreviewSchema,
    errors: ['ACC-002', 'REC-002', 'DIR-004', 'DIR-007', 'DIR-008', 'DIR-010', 'DIR-014'],
  },
  'directory.kindChanges.create': {
    method: 'POST',
    path: '/api/directory/kind-changes',
    access: 'session',
    operations: ['directory.write'],
    body: KindChangeBodySchema,
    status: 200,
    response: KindChangeResultSchema,
    errors: [
      'ACC-002',
      'REC-001',
      'REC-002',
      'DIR-004',
      'DIR-007',
      'DIR-008',
      'DIR-010',
      'DIR-014',
    ],
  },
  'directory.kindChanges.undo': {
    method: 'POST',
    path: '/api/directory/kind-changes/{id}/undo',
    access: 'session',
    operations: ['directory.write'],
    params: IdParamsSchema,
    status: 200,
    response: KindChangeResultSchema,
    errors: ['ACC-002', 'REC-002', 'DIR-004', 'DIR-008', 'DIR-009', 'DIR-010'],
  },
  'directory.kindDoubts.list': {
    method: 'GET',
    path: '/api/directory/kind-doubts',
    access: 'session',
    operations: ['directory.read'],
    query: KindDoubtsQuerySchema,
    status: 200,
    response: KindDoubtsPageSchema,
    errors: ['ACC-003'],
  },
  'directory.kindReviews.create': {
    method: 'POST',
    path: '/api/directory/kind-reviews',
    access: 'session',
    operations: ['directory.write'],
    body: KindReviewBodySchema,
    status: 200,
    response: KindReviewSchema,
    errors: ['REC-002', 'DIR-004', 'DIR-008'],
  },
  'directory.kindReviews.undo': {
    method: 'POST',
    path: '/api/directory/kind-reviews/undo',
    access: 'session',
    operations: ['directory.write'],
    body: KindReviewBodySchema,
    status: 204,
    errors: ['ACC-002', 'REC-002', 'DIR-004', 'DIR-008'],
  },

  // 006: подразделения и массовые действия (contracts/api.md 006).
  'directory.units.get': {
    method: 'GET',
    path: '/api/directory/units/{id}',
    access: 'session',
    operations: ['directory.read'],
    params: UnitParamsSchema,
    status: 200,
    response: UnitCardSchema,
    errors: ['ACC-002', 'REC-002'],
  },
  'directory.units.create': {
    method: 'POST',
    path: '/api/directory/units',
    access: 'session',
    operations: ['directory.decide'],
    body: CreateUnitBodySchema,
    status: 201,
    response: UnitCardSchema,
    errors: ['REC-002', 'DIR-004', 'DIR-008', 'DIR-012', 'DIR-013'],
  },
  'directory.units.update': {
    method: 'PATCH',
    path: '/api/directory/units/{id}',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateUnitBodySchema,
    status: 200,
    response: UnitCardSchema,
    errors: ['REC-001', 'REC-002', 'DIR-011', 'DIR-012', 'DIR-013'],
  },
  'directory.units.archivePreview': {
    method: 'GET',
    path: '/api/directory/units/{id}/archive-preview',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitParamsSchema,
    status: 200,
    response: UnitArchivePreviewSchema,
    errors: ['REC-002', 'DIR-013'],
  },
  'directory.units.archive': {
    method: 'POST',
    path: '/api/directory/units/{id}/archive',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitParamsSchema,
    headers: IfMatchHeadersSchema,
    status: 200,
    response: UnitArchiveResultSchema,
    errors: ['REC-001', 'REC-002', 'DIR-013'],
  },
  'directory.units.restore': {
    method: 'POST',
    path: '/api/directory/units/{id}/restore',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitParamsSchema,
    status: 200,
    response: UnitArchiveResultSchema,
    errors: ['REC-002', 'DIR-012', 'DIR-013'],
  },
  'directory.units.setEmail': {
    method: 'POST',
    path: '/api/directory/units/{id}/email',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitParamsSchema,
    body: SetUnitEmailBodySchema,
    status: 200,
    response: UnitCardSchema,
    errors: ['REC-002', 'DIR-013', 'DIR-001'],
  },
  'directory.bulk.apply': {
    method: 'POST',
    path: '/api/directory/bulk',
    access: 'session',
    operations: ['directory.write'],
    body: BulkBodySchema,
    status: 200,
    response: BulkResultSchema,
    errors: ['DIR-015'],
  },
  'directory.bulk.undo': {
    method: 'POST',
    path: '/api/directory/bulk/{id}/undo',
    access: 'session',
    operations: ['directory.write'],
    params: BulkParamsSchema,
    status: 200,
    response: BulkResultSchema,
    errors: ['ACC-002', 'REC-002', 'DIR-016'],
  },

  // 037: панель «Структура» и перевод организации в подразделение (contracts/api.md 037).
  'directory.structure': {
    method: 'GET',
    path: '/api/directory/structure',
    access: 'session',
    operations: ['directory.read'],
    status: 200,
    response: StaffStructureSchema,
    errors: ['ACC-003'],
  },
  'directory.unitConversions.preview': {
    method: 'GET',
    path: '/api/directory/organizations/{id}/unit-conversion-preview',
    access: 'session',
    operations: ['directory.decide'],
    params: IdParamsSchema,
    query: UnitConversionPreviewQuerySchema,
    status: 200,
    response: UnitConversionPreviewSchema,
    errors: ['REC-002', 'DIR-004', 'DIR-013', 'DIR-019', 'DIR-020'],
  },
  'directory.unitConversions.create': {
    method: 'POST',
    path: '/api/directory/unit-conversions',
    access: 'session',
    operations: ['directory.decide'],
    body: CreateUnitConversionBodySchema,
    status: 201,
    response: UnitConversionSchema,
    errors: ['REC-001', 'REC-002', 'DIR-004', 'DIR-012', 'DIR-013', 'DIR-019', 'DIR-020'],
  },
  'directory.unitConversions.undo': {
    method: 'POST',
    path: '/api/directory/unit-conversions/{id}/undo',
    access: 'session',
    operations: ['directory.decide'],
    params: UnitConversionParamsSchema,
    status: 200,
    response: UnitConversionSchema,
    errors: ['REC-002', 'DIR-012', 'DIR-021'],
  },

  // Настройки установки и ключи агента (005; обработчики — дорожка S2).
  'settings.list': {
    method: 'GET',
    path: '/api/settings',
    access: 'session',
    operations: ['settings.write'],
    status: 200,
    response: SettingsResponseSchema,
    errors: [],
  },
  'settings.update': {
    method: 'POST',
    path: '/api/settings/{key}',
    access: 'session',
    operations: ['settings.write'],
    params: SettingParamsSchema,
    body: UpdateSettingBodySchema,
    status: 200,
    response: SettingSchema,
    errors: ['SET-001', 'SET-002'],
  },
  'settings.history': {
    method: 'GET',
    path: '/api/settings/{key}/history',
    access: 'session',
    operations: ['settings.write'],
    params: SettingParamsSchema,
    status: 200,
    response: SettingHistorySchema,
    errors: [],
  },
  'agentKeys.list': {
    method: 'GET',
    path: '/api/agent-keys',
    access: 'session',
    operations: ['agent_keys.manage'],
    status: 200,
    response: AgentKeyListSchema,
    errors: [],
  },
  'agentKeys.issue': {
    method: 'POST',
    path: '/api/agent-keys',
    access: 'session',
    operations: ['agent_keys.manage'],
    body: IssueAgentKeyBodySchema,
    status: 201,
    response: IssuedAgentKeySchema,
    errors: [],
  },
  'agentKeys.revoke': {
    method: 'POST',
    path: '/api/agent-keys/{id}/revoke',
    access: 'session',
    operations: ['agent_keys.manage'],
    params: IdParamsSchema,
    status: 200,
    response: AgentKeySchema,
    errors: [],
  },

  // Канал почты (008: contracts/api.md, «Маршруты»). Сеть к Resend — только у обработчика задач:
  // проверка, отчёты и отправка — задачи, маршруты их ставят.
  'delivery.connection.get': {
    method: 'GET',
    path: '/api/delivery/connection',
    access: 'session',
    operations: ['mail.read'],
    status: 200,
    response: MailConnectionViewSchema,
    errors: [],
  },
  'delivery.connection.save': {
    method: 'PATCH',
    path: '/api/delivery/connection',
    access: 'session',
    operations: ['mail.write'],
    headers: MailConnectionVersionHeadersSchema,
    body: SaveMailConnectionBodySchema,
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['REC-001', 'REC-007', 'MAIL-008'],
  },
  'delivery.connection.setKey': {
    method: 'POST',
    path: '/api/delivery/connection/key',
    access: 'session',
    operations: ['mail.write'],
    body: SetMailKeyBodySchema,
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['MAIL-001', 'MAIL-003', 'MAIL-004'],
  },
  'delivery.connection.removeKey': {
    method: 'POST',
    path: '/api/delivery/connection/key/remove',
    access: 'session',
    operations: ['mail.write'],
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['MAIL-001'],
  },
  'delivery.connection.check': {
    method: 'POST',
    path: '/api/delivery/connection/check',
    access: 'session',
    operations: ['mail.write'],
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['MAIL-001', 'MAIL-004'],
  },
  'delivery.connection.registerReports': {
    method: 'POST',
    path: '/api/delivery/connection/reports',
    access: 'session',
    operations: ['mail.write'],
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['MAIL-001', 'MAIL-004'],
  },
  'delivery.connection.setSending': {
    method: 'POST',
    path: '/api/delivery/connection/sending',
    access: 'session',
    operations: ['mail.write'],
    body: SetMailSendingBodySchema,
    status: 200,
    response: MailConnectionViewSchema,
    errors: ['MAIL-001'],
  },
  'delivery.messages.testLetter': {
    method: 'POST',
    path: '/api/delivery/messages/test',
    access: 'session',
    operations: ['mail.write'],
    status: 201,
    response: MailMessageCardSchema,
    errors: ['MAIL-001', 'MAIL-002', 'MAIL-004', 'MAIL-016', 'MAIL-022'],
  },
  'delivery.messages.list': {
    method: 'GET',
    path: '/api/delivery/messages',
    access: 'session',
    operations: ['mail.read'],
    query: MailMessagesQuerySchema,
    status: 200,
    response: MailMessagesPageSchema,
    errors: ['REC-007'],
  },
  'delivery.messages.get': {
    method: 'GET',
    path: '/api/delivery/messages/{id}',
    access: 'session',
    operations: ['mail.read'],
    params: IdParamsSchema,
    status: 200,
    response: MailMessageCardSchema,
    errors: ['REC-002'],
  },

  // «Шаблоны писем» (042: contracts/api.md, «Маршруты»): слова писем каталога, правка
  // администратором с версиями, предпросмотр на учебных данных. {id} — запись или phrase:<id>.
  'letters.templates.list': {
    method: 'GET',
    path: '/api/letter-templates',
    access: 'session',
    operations: ['mail.templates'],
    status: 200,
    response: LetterTemplateListSchema,
    errors: [],
  },
  'letters.templates.read': {
    method: 'GET',
    path: '/api/letter-templates/{id}',
    access: 'session',
    operations: ['mail.templates'],
    params: LetterTemplateParamsSchema,
    status: 200,
    response: LetterTemplateSchema,
    errors: ['REC-002'],
  },
  'letters.templates.preview': {
    method: 'POST',
    path: '/api/letter-templates/{id}/preview',
    access: 'session',
    operations: ['mail.templates'],
    params: LetterTemplateParamsSchema,
    body: LetterPreviewBodySchema,
    status: 200,
    response: LetterPreviewSchema,
    errors: ['REC-002', 'REC-007'],
  },
  'letters.templates.update': {
    method: 'PUT',
    path: '/api/letter-templates/{id}',
    access: 'session',
    operations: ['mail.templates'],
    params: LetterTemplateParamsSchema,
    body: UpdateLetterTemplateBodySchema,
    status: 200,
    response: LetterTemplateSchema,
    errors: ['REC-002', 'REC-007', 'MAIL-024', 'MAIL-025', 'MAIL-026', 'MAIL-027'],
  },
  'letters.templates.restore': {
    method: 'POST',
    path: '/api/letter-templates/{id}/restore',
    access: 'session',
    operations: ['mail.templates'],
    params: LetterTemplateParamsSchema,
    body: RestoreLetterTemplateBodySchema,
    status: 200,
    response: LetterTemplateSchema,
    errors: ['REC-002', 'REC-007', 'MAIL-024'],
  },
  'letters.templates.history': {
    method: 'GET',
    path: '/api/letter-templates/{id}/history',
    access: 'session',
    operations: ['mail.templates'],
    params: LetterTemplateParamsSchema,
    status: 200,
    response: LetterHistorySchema,
    errors: ['REC-002'],
  },

  // «Первое письмо» (030: contracts/api.md, «Первое письмо»; research Р5): окно в протоколе —
  // кто получит и кто нет с причиной, отправка в 08:00 следующего рабочего дня, отмена до неё.
  'firstLetter.get': {
    method: 'GET',
    path: '/api/protocols/{id}/first-letter',
    access: 'session',
    operations: ['minutes.first_letter'],
    params: IdParamsSchema,
    status: 200,
    response: FirstLetterViewSchema,
    errors: ['MIN-001'],
  },
  'firstLetter.schedule': {
    method: 'POST',
    path: '/api/protocols/{id}/first-letter',
    access: 'session',
    operations: ['minutes.first_letter'],
    params: IdParamsSchema,
    body: EmptyBodySchema,
    status: 200,
    response: FirstLetterViewSchema,
    errors: ['MIN-001', 'MAIL-001', 'MAIL-002', 'MAIL-019'],
  },
  'firstLetter.cancel': {
    method: 'DELETE',
    path: '/api/protocols/{id}/first-letter',
    access: 'session',
    operations: ['minutes.first_letter'],
    params: IdParamsSchema,
    status: 200,
    response: FirstLetterViewSchema,
    errors: ['MIN-001', 'MAIL-020'],
  },

  // Стоп-кран (033: contracts/api.md, «Стоп-кран»; research Р1): состояние над всеми каналами.
  // Повтор «Остановить» и «Возобновить» — 200 без журнала.
  'delivery.safety.get': {
    method: 'GET',
    path: '/api/delivery/safety',
    access: 'session',
    operations: ['mail.stop'],
    status: 200,
    response: MailSafetySchema,
    errors: [],
  },
  'delivery.safety.stop': {
    method: 'POST',
    path: '/api/delivery/safety/stop',
    access: 'session',
    operations: ['mail.stop'],
    body: EmptyBodySchema,
    status: 200,
    response: MailSafetySchema,
    errors: [],
  },
  'delivery.safety.resume': {
    method: 'POST',
    path: '/api/delivery/safety/resume',
    access: 'session',
    operations: ['mail.stop'],
    body: EmptyBodySchema,
    status: 200,
    response: MailSafetySchema,
    errors: [],
  },

  // Рассылка протокола (033: contracts/api.md, «Рассылка протокола»; FR-010 – FR-013): право на
  // протокол — по его области, как «Первое письмо»; «включена» — только из «в тени».
  'mailing.get': {
    method: 'GET',
    path: '/api/protocols/{id}/mailing',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    status: 200,
    response: ProtocolMailingSchema,
    errors: ['REC-002'],
  },
  'mailing.set': {
    method: 'POST',
    path: '/api/protocols/{id}/mailing',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    body: MailingSetBodySchema,
    status: 200,
    response: ProtocolMailingSchema,
    errors: ['REC-002', 'MAIL-023'],
  },
  'mailing.enable': {
    method: 'POST',
    path: '/api/protocols/{id}/mailing/enable',
    access: 'session',
    operations: ['minutes.mailing_enable'],
    params: IdParamsSchema,
    body: MailingEnableBodySchema,
    status: 200,
    response: ProtocolMailingSchema,
    errors: ['REC-002', 'MAIL-023'],
  },

  // Готовность к рассылке (031: contracts/api.md, «Маршруты»; FR-013, FR-016, FR-018, FR-023):
  // окно протокола — люди по группам, письма в тени, воронка, день планёрки.
  'readiness.get': {
    method: 'GET',
    path: '/api/protocols/{id}/readiness',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    status: 200,
    response: ProtocolReadinessSchema,
    errors: ['REC-002'],
  },
  'readiness.shadowLetters': {
    method: 'GET',
    path: '/api/protocols/{id}/shadow-letters',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    status: 200,
    response: ShadowLettersSchema,
    errors: ['REC-002'],
  },
  'readiness.funnel': {
    method: 'GET',
    path: '/api/protocols/{id}/funnel',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    status: 200,
    response: ProtocolFunnelSchema,
    errors: ['REC-002', 'MIN-001'],
  },
  'mailing.meetingDay': {
    method: 'POST',
    path: '/api/protocols/{id}/meeting-day',
    access: 'session',
    operations: ['minutes.mailing'],
    params: IdParamsSchema,
    body: MeetingDayBodySchema,
    status: 200,
    response: MeetingDaySchema,
    errors: ['REC-002'],
  },

  // Схемы шаблонов и мастер подключения протокола (010: contracts/api.md, «Маршруты»). Вопросы
  // мастера задаются в запросе администратора, без фоновой задачи (research, Р4).
  'templates.list': {
    method: 'GET',
    path: '/api/templates',
    access: 'session',
    operations: ['templates.read'],
    query: TemplateListQuerySchema,
    status: 200,
    response: TemplateListSchema,
    errors: [],
  },
  'templates.get': {
    method: 'GET',
    path: '/api/templates/{id}',
    access: 'session',
    operations: ['templates.read'],
    params: IdParamsSchema,
    status: 200,
    response: TemplateCardSchema,
    errors: ['REC-002'],
  },
  'templates.onboarding.analyze': {
    method: 'POST',
    path: '/api/templates/onboarding',
    access: 'session',
    operations: ['templates.write'],
    upload: 'xlsx',
    headers: UploadHeadersSchema,
    query: OnboardingAnalyzeQuerySchema,
    status: 200,
    response: OnboardingDraftSchema,
    // Сверх api.md 010: файл больше предела — как у загрузки протокола (003).
    errors: ['DOC-001', 'DOC-003', 'TPL-001', 'TPL-007'],
  },
  'templates.onboarding.rows': {
    method: 'POST',
    path: '/api/templates/onboarding/rows',
    access: 'session',
    operations: ['templates.write'],
    body: OnboardingRowsBodySchema,
    status: 200,
    response: RowsViewSchema,
    errors: ['TPL-005', 'REC-007'],
  },
  'templates.create': {
    method: 'POST',
    path: '/api/templates',
    access: 'session',
    operations: ['templates.write'],
    body: CreateTemplateBodySchema,
    status: 201,
    response: TemplateCardSchema,
    errors: ['TPL-002', 'TPL-003', 'TPL-004', 'TPL-005'],
  },
  'templates.versions.create': {
    method: 'POST',
    path: '/api/templates/{id}/versions',
    access: 'session',
    operations: ['templates.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: NewTemplateVersionBodySchema,
    status: 201,
    response: TemplateCardSchema,
    errors: ['REC-001', 'REC-002', 'TPL-002', 'TPL-005'],
  },
  'templates.update': {
    method: 'PATCH',
    path: '/api/templates/{id}',
    access: 'session',
    operations: ['templates.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    body: UpdateTemplateBodySchema,
    status: 200,
    response: TemplateCardSchema,
    errors: ['REC-001', 'REC-002', 'REC-007'],
  },
  'templates.archive': {
    method: 'POST',
    path: '/api/templates/{id}/archive',
    access: 'session',
    operations: ['templates.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    status: 200,
    response: TemplateCardSchema,
    errors: ['REC-001', 'REC-002'],
  },
  'templates.restore': {
    method: 'POST',
    path: '/api/templates/{id}/restore',
    access: 'session',
    operations: ['templates.write'],
    params: IdParamsSchema,
    headers: IfMatchHeadersSchema,
    status: 200,
    response: TemplateCardSchema,
    errors: ['REC-001', 'REC-002', 'TPL-003'],
  },

  // 032: «Входящие», ответ из письма, руководитель, отъезд (contracts/api.md 032, «Маршруты»).
  // Страница ответа и отписка — HTML-страницы модуля answers без React (/answer/, /u/), не API.
  'inbox.card': {
    method: 'GET',
    path: '/api/inbox/{id}',
    access: 'session',
    operations: ['notifications.read'],
    params: IdParamsSchema,
    status: 200,
    response: InboxCardSchema,
    errors: ['REC-002'],
  },
  'answers.enter': {
    method: 'POST',
    path: '/api/answers/enter',
    access: 'session',
    operations: ['minutes.edit'],
    body: AnswerEnterSchema,
    status: 200,
    response: AnswerEnterResultSchema,
    // MIN-021 — ответ-«проверка» (письмо песочницы): пункт не меняется (принцип IV, FR-012).
    errors: ['REC-001', 'REC-002', 'MIN-021', 'MIN-023', 'MIN-024'],
  },
  'team.overdue': {
    method: 'GET',
    path: '/api/team/overdue',
    access: 'session',
    operations: ['items.team'],
    status: 200,
    response: TeamOverdueSchema,
    errors: ['ACC-008'],
  },
  'my.away.set': {
    method: 'PUT',
    path: '/api/my/away',
    access: 'session',
    operations: ['items.own'],
    body: AbsenceBodySchema,
    status: 200,
    response: AbsenceSchema,
    errors: ['ACC-008', 'REC-007'],
  },
  'my.away.clear': {
    method: 'DELETE',
    path: '/api/my/away',
    access: 'session',
    operations: ['items.own'],
    status: 204,
    errors: ['ACC-008'],
  },
  'people.away.set': {
    method: 'PUT',
    path: '/api/people/{personId}/away',
    access: 'session',
    operations: ['people.away'],
    params: PersonParamsSchema,
    body: AbsenceBodySchema,
    status: 200,
    response: AbsenceSchema,
    errors: ['REC-002', 'REC-007'],
  },
  'people.away.clear': {
    method: 'DELETE',
    path: '/api/people/{personId}/away',
    access: 'session',
    operations: ['people.away'],
    params: PersonParamsSchema,
    status: 204,
    errors: ['REC-002'],
  },
  'people.loginLink': {
    method: 'POST',
    path: '/api/people/{personId}/login-link',
    access: 'session',
    operations: ['directory.write'],
    params: PersonParamsSchema,
    body: EmptyBodySchema,
    status: 204,
    errors: ['REC-002', 'MAIL-011'],
  },

  // Проверка состояния: «жив» и «готов» (002, contracts/api.md, «Проверка состояния»).
  'system.health': {
    method: 'GET',
    path: '/api/health',
    access: 'public',
    operations: [],
    status: 200,
    response: HealthResponseSchema,
    errors: [],
  },
  'system.ready': {
    method: 'GET',
    path: '/api/health/ready',
    access: 'public',
    operations: [],
    status: 200,
    response: ReadyResponseSchema,
    // Не готов — 503 { status: "not_ready" }, а не SYS-001 (contracts/api.md 002).
    unavailable: NotReadyResponseSchema,
    errors: [],
  },
} as const satisfies Record<string, RouteDefinition>;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

export const routeNames = Object.keys(routes) as RouteName[];

/**
 * Маршруты сверх contracts/api.md 001, 002, 003 и 005. Проверка состояния /api/health была
 * дополнением 001; с 002 оба адреса проверки состояния описаны в api.md. Версии документа для
 * боковой панели — дополнение приёмки 0.3.1 (003).
 */
export const extensionRoutes = [
  'documents.versions.list',
  'manager.list',
  'manager.folders.create',
  'manager.update',
  'manager.favorite',
  'manager.batch',
  'manager.activity',
  'manager.comments.create',
  'manager.comments.update',
  'manager.comments.delete',
] as const satisfies readonly RouteName[];

type SchemaIn<R, K extends string> = R extends { readonly [P in K]: infer S extends z.ZodType }
  ? z.input<S>
  : undefined;

/** Что отправляет клиент: параметры пути, строка запроса, тело. */
export type RouteParams<N extends RouteName> = SchemaIn<Routes[N], 'params'>;
export type RouteQuery<N extends RouteName> = SchemaIn<Routes[N], 'query'>;
export type RouteBody<N extends RouteName> = SchemaIn<Routes[N], 'body'>;

/** Что получает клиент: JSON по схеме ответа; пустой ответ — undefined, файл — байты. */
export type RouteResponse<N extends RouteName> = Routes[N] extends {
  readonly response: infer S extends z.ZodType;
}
  ? z.output<S>
  : Routes[N] extends { readonly file: string }
    ? ArrayBuffer
    : undefined;

export function isMutating(method: HttpMethod): boolean {
  return method !== 'GET';
}

/** /api/users/{id} → /api/users/:id. */
export function toFastifyPath(path: string): string {
  return path.replace(/\{(\w+)\}/g, ':$1');
}

/** Подставляет параметры в путь с кодированием: buildPath('/api/users/{id}', { id }). */
export function buildPath(path: string, params: Readonly<Record<string, string>> = {}): string {
  return path.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`Нет параметра ${key} для ${path}`);
    return encodeURIComponent(value);
  });
}

/**
 * Все ошибки маршрута: из api.md и общие — сеанс закончился (AUTH-004), нет права (ACC-001,
 * ACC-002 для записи), нет записи (REC-002), форма (REC-007), чужой источник для изменяющих
 * запросов (SYS-002), сервис недоступен (SYS-001) — кроме маршрута со своим ответом 503
 * (unavailable: «готов»). Порядок — как в каталоге.
 */
export function routeErrors(route: RouteDefinition): ErrorCode[] {
  const found = new Set<ErrorCode>(route.errors);
  const hasId = route.path.includes('{id}');
  if (route.access === 'session') found.add('AUTH-004');
  if (route.operations.length > 0) {
    found.add('ACC-001');
    if (hasId) found.add('ACC-002');
  }
  if (hasId) found.add('REC-002');
  if (route.params || route.query || route.body || route.headers) found.add('REC-007');
  if (isMutating(route.method)) found.add('SYS-002');
  if (!route.unavailable) found.add('SYS-001');
  return errorCodes.filter((code) => found.has(code));
}
